import { useCallback, useEffect, useState } from 'react';

const INIT_ACTION = {
  type: 'INIT',
};

export default function useEditorActions({
  updateVideoPartsScenes: updateScenes,
  updateTransitions,
  moveScenes,
  handleChanged = () => { },
  updateIntroOutro = () => { },
  updateLogo = () => { },
  updateSceneIndex = () => { },
}) {
  const [actionsStorage, setActionsStorage] = useState([INIT_ACTION]);
  const [currentActionIndex, setCurrentActionIndex] = useState(0);
  const [controlsDisabled, setControlsDisabled] = useState({ undo: false, redo: false });

  const registerNewAction = useCallback(
    action => {
      setActionsStorage(prevState => {
        if (currentActionIndex < prevState.length - 1) {
          return currentActionIndex === 0 ? [INIT_ACTION, action] : [...prevState.slice(0, currentActionIndex + 1), action];
        } else {
          return [...prevState, action];
        }
      });
      handleChanged();
      setCurrentActionIndex(prevState => prevState + 1);
    },
    [currentActionIndex, setActionsStorage]
  );

  const newRenameAction = useCallback(
    (scene, changes) => {
      registerNewAction({ type: 'RENAME', scene, ...changes });
    },
    [registerNewAction]
  );

  const newAddAction = useCallback(
    (scene, index, before) => {
      registerNewAction({ type: 'ADD', scene, index, before });
    },
    [registerNewAction]
  );

  const newDeleteAction = useCallback(
    (scene, index, sceneTransitions) => {
      registerNewAction({ type: 'DELETE', scene, index, sceneTransitions });
    },
    [currentActionIndex, registerNewAction]
  );

  const newDuplicateAction = useCallback(
    scene => {
      registerNewAction({ type: 'DUPLICATE', scene });
    },
    [registerNewAction]
  );

  const newMoveAction = useCallback(
    (scene, oldIndex, newIndex) => {
      registerNewAction({ type: 'MOVE', scene, oldIndex, newIndex });
    },
    [registerNewAction]
  );

  const newTrimAction = useCallback(
    (scene, newTrimStart, newTrimEnd) => {
      registerNewAction({ type: 'TRIM', scene, newTrimStart, newTrimEnd });
    },
    [registerNewAction, currentActionIndex]
  );

  const newIntroOutroAction = useCallback(
    ({ introOutro, previousIntroOutro }) => {
      registerNewAction({ type: 'INTRO_OUTRO', introOutro, previousIntroOutro });
    },
    [registerNewAction]
  );

  const newTextPanelAction = useCallback(
    (index, topText, bottomText, previousNameCard) => {
      registerNewAction({ type: 'TEXT_PANEL', index, topText, bottomText, previousNameCard });
    },
    [registerNewAction]
  );

  const newTransitionAction = useCallback(
    transition => {
      registerNewAction({ type: 'ADD_TRANSITION', transition });
    },
    [registerNewAction]
  );

  const newDeleteTransitionAction = useCallback(
    deletedTransition => {
      registerNewAction({ type: 'REMOVE_TRANSITION', deletedTransition });
    },
    [registerNewAction]
  );

  const updateTransitionAction = useCallback(
    (rightMediaId, transitionType, newTransitionType) => {
      registerNewAction({ type: 'UPDATE_TRANSITION', rightMediaId, transitionType, newTransitionType });
    },
    [registerNewAction]
  );

  const newBackgroundMusicAction = useCallback(
    ({ sceneIndex, newMusic, musicVolume, videoVolume, isApplyAllTrackEnabled, isApplyAllVolumeEnabled, previousScenes }) => {
      registerNewAction({
        type: 'BACKGROUND_MUSIC',
        sceneIndex,
        newMusic,
        musicVolume,
        videoVolume,
        isApplyAllTrackEnabled,
        isApplyAllVolumeEnabled,
        previousScenes,
      });
    },
    [registerNewAction]
  );

  const newSplitAction = useCallback(
    (splittedSceneIndex, splitTime, trimStartBeforeSplit, trimEndBeforeSplit, addedScene, trimStart, trimEnd) => {
      registerNewAction({
        type: 'SPLIT',
        splittedSceneIndex,
        splitTime,
        trimStartBeforeSplit,
        trimEndBeforeSplit,
        addedScene,
        trimStart,
        trimEnd,
      });
    },
    [registerNewAction]
  );

  const newSpeedUpAction = useCallback(
    ({ sceneIndex, playbackRate, previousPlaybackRate }) => {
      registerNewAction({
        type: 'SPEED_UP',
        sceneIndex,
        playbackRate,
        previousPlaybackRate,
      });
    },
    [registerNewAction]
  );

  const newLogoAction = useCallback(
    ({ logo, prevLogo }) => {
      registerNewAction({
        type: 'LOGO',
        logo,
        prevLogo,
      });
    },
    [registerNewAction]
  );

  const newZoomPointsAction = useCallback(
    ({ sceneIndex, zoomPoints, previousZoomPoints }) => {
      registerNewAction({
        type: 'ZOOM_POINTS',
        sceneIndex,
        zoomPoints,
        previousZoomPoints,
      });
    },
    [registerNewAction]
  );

  const undoLastAction = () => {
    console.log('undoLastAction currentActionIndex', currentActionIndex);
    if (currentActionIndex >= 1) {
      const currentAction = actionsStorage[currentActionIndex];
      if (currentAction?.type) {
        switch (currentAction.type) {
          case 'ADD':
            const { before } = currentAction;
            updateScenes(prevState => {
              const updatedArray = Array.from(prevState);
              // Get scenes before added added one
              const firstAddedElement = updatedArray.find(item => item.id === currentAction.scene[0].id);
              if (firstAddedElement) {
                const firstAddedElementIndex = updatedArray.indexOf(firstAddedElement);
                let lastAddedElement = firstAddedElement;
                let lastAddedElementIndex = firstAddedElementIndex;
                if (currentAction.scene.length > 1) {
                  lastAddedElement = updatedArray.find(scene => scene.id === currentAction.scene[currentAction.scene.length - 1].id);
                  lastAddedElementIndex = updatedArray.indexOf(lastAddedElement);
                }
                const beforeArray = updatedArray.slice(0, firstAddedElementIndex);
                const afterArray = updatedArray.slice(lastAddedElementIndex + 1, updatedArray.length);
                updateTransitions(prevState =>
                  prevState.map(item => {
                    if (before) {
                      if (item.rightMediaId === updatedArray[firstAddedElementIndex].id) {
                        return { ...item, rightMediaId: updatedArray[lastAddedElementIndex + 1].id };
                      }
                    } else {
                      if (item.leftMediaId === updatedArray[lastAddedElementIndex].id) {
                        return {
                          ...item,
                          rightMediaId: updatedArray[lastAddedElementIndex + 1].id,
                          leftMediaId: updatedArray[firstAddedElementIndex - 1].id,
                        };
                      }
                    }
                    return item;
                  })
                );
                updateSceneIndex(Math.max(beforeArray.length - 1, 0));
                return [...beforeArray, ...afterArray];
              }
            });
            break;
          case 'DELETE':
            const { scene: removedScene, index, sceneTransitions } = currentAction;
            updateScenes(prevState => {
              const scenesBefore = prevState.slice(0, index);
              updateSceneIndex(scenesBefore.length);
              return [...scenesBefore, removedScene, ...prevState.slice(index, prevState.length)];
            });
            updateTransitions(prevState => [...prevState, ...sceneTransitions]);
            break;
          case 'DUPLICATE':
            updateScenes(prevState => prevState.filter(item => item.id !== currentAction.scene.id));
            break;
          case 'RENAME':
            updateScenes(prevState => prevState.map(item => (item.id === currentAction.scene.id ? currentAction.scene : item)));
            break;
          case 'MOVE':
            moveScenes(currentAction.scene, currentAction.newIndex, currentAction.oldIndex, false);
            break;
          case 'TRIM':
            const { scene } = currentAction;
            updateScenes(prevState =>
              prevState.map(item =>
                item.id === scene.id
                  ? {
                    ...item,
                    trimStart: scene.trimStart,
                    trimEnd: scene.trimEnd,
                    trimmedDuration: scene.trimEnd - scene.trimStart,
                  }
                  : item
              )
            );
            break;
          case 'SPLIT':
            const { splittedSceneIndex, trimStartBeforeSplit, trimEndBeforeSplit, addedScene } = currentAction;
            updateScenes(prevState =>
              prevState
                .map((item, index) =>
                  index === splittedSceneIndex
                    ? {
                      ...item,
                      trimEnd: trimEndBeforeSplit,
                      trimStart: trimStartBeforeSplit,
                      trimmedDuration: trimEndBeforeSplit - trimStartBeforeSplit,
                    }
                    : item
                )
                .filter(item => item.id !== addedScene.id)
            );
            break;
          case 'INTRO_OUTRO': {
            const { previousIntroOutro } = currentAction;
            updateIntroOutro({ introOutro: previousIntroOutro, undoOrRedoAction: true });
            break;
          }
          case 'ADD_TRANSITION': {
            const { transition } = currentAction;
            updateTransitions(prevState => prevState.filter(item => item.leftMediaId !== transition.leftMediaId));
            break;
          }
          case 'REMOVE_TRANSITION': {
            const { deletedTransition } = currentAction;
            updateTransitions(prevState => [...prevState, deletedTransition]);
            break;
          }
          case 'UPDATE_TRANSITION': {
            const { rightMediaId, transitionType } = currentAction;
            updateTransitions(prevState =>
              prevState.map(item => (item.rightMediaId === rightMediaId ? { ...item, type: transitionType } : item))
            );
            break;
          }
          case 'TEXT_PANEL': {
            const { index, previousNameCard } = currentAction;
            updateScenes(prevState =>
              prevState.map((item, itemIndex) =>
                index === itemIndex ? { ...item, nameCard: previousNameCard ? previousNameCard : null } : item
              )
            );
            break;
          }
          case 'BACKGROUND_MUSIC': {
            const { previousScenes } = currentAction;
            updateScenes(() => previousScenes);
            break;
          }
          case 'SPEED_UP': {
            const { sceneIndex, previousPlaybackRate } = currentAction;
            updateScenes(prevState =>
              prevState.map((item, itemIndex) =>
                sceneIndex === itemIndex ? { ...item, playbackRate: previousPlaybackRate || 1, speedUpChanged: false } : item
              )
            );
            break;
          }
          case 'LOGO': {
            const { prevLogo } = currentAction;
            updateLogo(prevLogo);
            break;
          }
          case 'ZOOM_POINTS': {
            const { sceneIndex, previousZoomPoints } = currentAction;
            updateScenes(prevState =>
              prevState.map((item, itemIndex) =>
                sceneIndex === itemIndex ? { ...item, zoomPoints: previousZoomPoints || [], zoomPointsChanged: false } : item
              )
            );
            break;
          }
          default:
            break;
        }
      }
      setCurrentActionIndex(prevState => prevState - 1);
    }
  };

  const redoLastAction = () => {
    console.log('redoLastAction');
    if (currentActionIndex < actionsStorage.length - 1) {
      const currentAction = actionsStorage[currentActionIndex + 1];
      switch (currentAction.type) {
        case 'ADD':
          const { before } = currentAction;
          updateScenes(prevState => {
            const scenesBefore =
              currentAction.index === 0 && currentAction.before
                ? []
                : Array.from(prevState).slice(0, currentAction.before ? currentAction.index : currentAction.index + 1);
            const scenesAfter = Array.from(prevState).slice(
              currentAction.before ? currentAction.index : currentAction.index + 1,
              prevState.length
            );
            // Rely on shareModal index and before flag insert stories
            updateTransitions(prevState =>
              prevState.map(item => {
                if (item => item.leftMediaId === scenesBefore[scenesBefore.length - 1].id) {
                  let leftMediaId = item.leftMediaId;
                  let rightMediaId = currentAction.scene[0].id;
                  if (!before) {
                    rightMediaId = scenesAfter[0].id;
                    leftMediaId = currentAction.scene[0].id;
                  }
                  return { ...item, leftMediaId, rightMediaId };
                }
                return item;
              })
            );
            updateSceneIndex(scenesBefore.length);
            return [...scenesBefore, ...currentAction.scene, ...scenesAfter];
          });
          break;
        case 'DELETE':
          updateScenes(prevState => {
            const itemIndex = prevState.findIndex(item => item.id === currentAction.scene.id);
            updateSceneIndex(Math.max(itemIndex - 1, 0));
            return prevState.filter(item => item.id !== currentAction.scene.id);
          });
          updateTransitions(prevState =>
            prevState.filter(item => item.rightMediaId !== currentAction.scene.id && item.leftMediaId !== currentAction.scene.id)
          );
          break;
        case 'DUPLICATE':
          updateScenes(prevState => [...prevState, currentAction.scene]);
          break;
        case 'RENAME':
          updateScenes(prevState =>
            prevState.map(item =>
              item.id === currentAction.scene.id
                ? { ...currentAction.scene, name: currentAction.name, nameChanged: currentAction.nameChanged }
                : item
            )
          );
          break;
        case 'MOVE':
          moveScenes(currentAction.scene, currentAction.oldIndex, currentAction.newIndex, false);
          break;
        case 'TRIM':
          const { scene, newTrimStart, newTrimEnd } = currentAction;
          updateScenes(prevState =>
            prevState.map(item =>
              item.id === scene.id
                ? { ...item, trimStart: newTrimStart, trimEnd: newTrimEnd, trimmedDuration: newTrimEnd - newTrimStart }
                : item
            )
          );
          break;
        case 'SPLIT':
          const { splittedSceneIndex, splitTime, addedScene, trimStart } = currentAction;
          updateScenes(prevState => {
            const leftScene = structuredClone(prevState[splittedSceneIndex]);
            leftScene.trimStart = trimStart;
            leftScene.trimEnd = splitTime;
            leftScene.trimmedDuration = leftScene.trimEnd - leftScene.trimStart;
            const scenesBefore = prevState.slice(0, splittedSceneIndex);
            const scenesAfter = prevState.slice(splittedSceneIndex + 1, prevState.length);
            return [...scenesBefore, leftScene, addedScene, ...scenesAfter];
          });
          break;
        case 'INTRO_OUTRO': {
          const { introOutro } = currentAction;
          updateIntroOutro({ introOutro, undoOrRedoAction: true });
          break;
        }
        case 'ADD_TRANSITION': {
          console.log('redoLastAction ADD_TRANSITION');

          const { transition } = currentAction;
          updateTransitions(prevState => [...prevState, transition]);
          break;
        }
        case 'REMOVE_TRANSITION': {
          console.log('redoLastAction REMOVE_TRANSITION');

          const { deletedTransition } = currentAction;
          updateTransitions(prevState => prevState.filter(item => item.leftMediaId !== deletedTransition.leftMediaId));
          break;
        }
        case 'UPDATE_TRANSITION': {
          const { rightMediaId, newTransitionType } = currentAction;
          console.log('redoLastAction UPDATE_TRANSITION', { newTransitionType, rightMediaId });
          updateTransitions(prevState =>
            prevState.map(item => (item.rightMediaId === rightMediaId ? { ...item, type: newTransitionType } : item))
          );
          break;
        }
        case 'TEXT_PANEL': {
          const { index, topText, bottomText } = currentAction;
          const isNull = !topText.trim() && !bottomText.trim();
          updateScenes(prevState =>
            prevState.map((item, itemIndex) =>
              itemIndex === index ? { ...item, nameCard: isNull ? null : { topText, bottomText } } : item
            )
          );
          break;
        }
        case 'BACKGROUND_MUSIC': {
          const { sceneIndex, newMusic, musicVolume, videoVolume, isApplyAllTrackEnabled, isApplyAllVolumeEnabled } = currentAction;
          updateScenes(prevState =>
            prevState.map((item, itemIndex) => {
              if (itemIndex === sceneIndex) {
                return {
                  ...item,
                  backgroundMusicChanged: true,
                  music: newMusic ? { ...newMusic, volume: musicVolume } : null,
                  videoVolume,
                };
              }

              // We are forced to do this checks, because when the 'Apply to all' for tracks turned off
              // and the 'Apply to all' for track's volume is turned on,
              // the music doesn't change, but music's volume changes,
              // and the field of which is located inside the music object should be changed without changing music itself

              const initialMusicVolume = 0.5;

              const newAppliedMusic = newMusic
                ? {
                  ...newMusic,
                  volume: isApplyAllVolumeEnabled ? musicVolume : item?.music?.volume ?? initialMusicVolume,
                }
                : null;
              const currentMusic = item.music
                ? {
                  ...item.music,
                  volume: isApplyAllVolumeEnabled ? musicVolume : item?.music?.volume ?? initialMusicVolume,
                }
                : null;

              return {
                ...item,
                backgroundMusicChanged: item.backgroundMusicChanged || isApplyAllTrackEnabled || isApplyAllVolumeEnabled,
                music: isApplyAllTrackEnabled ? newAppliedMusic : currentMusic,
                ...(isApplyAllVolumeEnabled && { videoVolume }),
              };
            })
          );
          break;
        }
        case 'SPEED_UP': {
          const { sceneIndex, playbackRate } = currentAction;
          updateScenes(prevState =>
            prevState.map((item, itemIndex) => (sceneIndex === itemIndex ? { ...item, playbackRate, speedUpChanged: true } : item))
          );
          break;
        }
        case 'LOGO': {
          const { logo } = currentAction;
          updateLogo(logo);
          break;
        }
        case 'ZOOM_POINTS': {
          const { sceneIndex, zoomPoints } = currentAction;
          updateScenes(prevState =>
            prevState.map((item, itemIndex) => (sceneIndex === itemIndex ? { ...item, zoomPoints, zoomPointsChanged: true } : item))
          );
          break;
        }
        default:
          break;
      }
      setCurrentActionIndex(prevState => prevState + 1);
    }
  };

  const updateSceneResources = useCallback(
    updatedScene => {
      if (updatedScene) {
        setActionsStorage(prevState => {
          return prevState?.map(action => {
            const { type, scene } = action;
            if (type === 'ADD') {
              const newScenes = scene?.map(scene => {
                if (scene.id.includes(updatedScene.id)) {
                  return { ...updatedScene };
                } else {
                  return scene;
                }
              });
              return { type, scene: newScenes };
            }
            return action;
          });
        });
      }
    },
    [actionsStorage]
  );

  const clearStorage = useCallback(() => {
    setActionsStorage([INIT_ACTION]);
    setCurrentActionIndex(0);
  }, []);

  useEffect(() => {
    setControlsDisabled({
      undo: currentActionIndex === 0,
      redo: currentActionIndex >= actionsStorage.length - 1 || actionsStorage.length === 1,
    });
  }, [currentActionIndex]);

  return {
    currentActionIndex,
    newRenameAction,
    newAddAction,
    newDeleteAction,
    newDuplicateAction,
    newMoveAction,
    newTrimAction,
    newSplitAction,
    newTextPanelAction,
    undoLastAction,
    redoLastAction,
    currentActionIndex,
    controlsDisabled,
    updateSceneResources,
    clearStorage,
    newIntroOutroAction,
    newTransitionAction,
    newDeleteTransitionAction,
    updateTransitionAction,
    newBackgroundMusicAction,
    newSpeedUpAction,
    newLogoAction,
    newZoomPointsAction,
  };
}
